import React, { useState, useRef } from "react";
import {
  Box,
  Heading,
  Text,
  Input,
  Textarea,
  Button,
  FormControl,
  FormLabel,
  VStack,
  SimpleGrid,
  useToast,
  Flex,
  Image,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import emailjs from "emailjs-com";
import formImage from "../assets/applynow.jpg";
import cultureImg from "../assets/culture.jpg";
import timeOffImg from "../assets/timeoffs.jpg";
import heroImg from "../assets/careers.jpg";

const MotionBox = motion(Box);

export default function Careers() {
  const toast = useToast();
  const formRef = useRef();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    coverLetter: "",
    cv: null,
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "cv") {
      const file = files[0];
      setFormData((prev) => ({ ...prev, cv: file }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const sendEmail = (e) => {
    e.preventDefault();
    setLoading(true);

    // Company email with all details
    const messageDetails = `
      Name: ${formData.name}
      Email: ${formData.email}
      Phone: ${formData.phone}
      Cover Letter: ${formData.coverLetter}
      CV: ${formData.cv ? formData.cv.name : "CV Uploaded (not attached)"}
    `;

    emailjs
      .send(
        "service_m94cmwa",
        "template_piqmqsj",
        { name: formData.name, message: messageDetails },
        "2ZRwVGJtWzOCOyxRr"
      )
      .then(() => {
        // Applicant confirmation email
        return emailjs.send(
          "service_m94cmwa",
          "template_v4lo3fh",
          {
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            coverLetter: formData.coverLetter,
            cv: formData.cv ? formData.cv.name : "CV Uploaded (not attached)",
          },
          "2ZRwVGJtWzOCOyxRr"
        );
      })
      .then(() => {
        setLoading(false);
        toast({
          title: "Application Submitted",
          description: "Thank you! We'll review your application soon.",
          status: "success",
          duration: 4000,
          isClosable: true,
        });
        setFormData({
          name: "",
          email: "",
          phone: "",
          coverLetter: "",
          cv: null,
        });
        if (formRef.current) formRef.current.reset();
      })
      .catch((err) => {
        console.error("Email send error:", err);
        setLoading(false);
        toast({
          title: "Submission Failed",
          description: "Please try again later.",
          status: "error",
          duration: 4000,
          isClosable: true,
        });
      });
  };

  return (
    <Box>
      <Box>
        {/* Hero Section */}
        <MotionBox
          position="relative"
          shadow="xl"
          mb={10}
          textAlign="center"
          overflow="hidden"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
          minH={{ base: "300px", md: "350px" }}
        >
          <Box
            position="absolute"
            top={0}
            left={0}
            width="100%"
            height="100%"
            bgImage={`url(${heroImg})`}
            bgSize="cover"
            bgPosition="center"
            filter="brightness(0.6) blur(2px)"
            zIndex={0}
          />
          <Box
            position="relative"
            zIndex={1}
            color="white"
            p={{ base: 10, md: 16 }}
          >
            <Heading fontSize={{ base: "3xl", md: "5xl" }} mb={4}>
              Shine Bright with HoriSun Renewables
            </Heading>
            <Text
              fontWeight="bold"
              fontSize={{ base: "md", md: "lg" }}
              mt={4}
              maxW="3xl"
              mx="auto"
            >
              Join our team to make renewable energy sustainable and innovative.
            </Text>
          </Box>
        </MotionBox>

        <SimpleGrid
          columns={{ base: 1, md: 2 }}
          spacing={10}
          px={{ base: 4, md: 20 }}
          mb={16}
        >
          {[
            {
              title: "Our Culture",
              image: cultureImg,
              items: [
                "Welcoming Workplace: Teamwork, innovation, and sustainability.",
                "Collaboration: Great ideas come from working together.",
                "Innovation: Creative solutions and experimentation.",
                "Diversity: Inclusive environment for all backgrounds.",
              ],
            },
            {
              title: "Time Off & Benefits",
              image: timeOffImg,
              items: [
                "Flexible working hours.",
                "Monday-to-Friday schedule.",
                "Annual team excursions.",
                "Employee mental health is important to us",
              ],
            },
          ].map((section, idx) => (
            <MotionBox
              key={idx}
              bg="white"
              rounded="2xl"
              shadow="xl"
              overflow="hidden"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: idx * 0.2 }}
            >
              <Box>
                <Box
                  as="img"
                  src={section.image}
                  alt={section.title}
                  objectFit="cover"
                  h="250px"
                  w="100%"
                />
                <Box p={6}>
                  <Heading fontSize="2xl" color="#065F46" mb={6}>
                    {section.title}
                  </Heading>
                  <VStack align="start" spacing={4}>
                    {section.items.map((item, i) => (
                      <MotionBox
                        key={i}
                        bg="#E0F2FE"
                        color="#065F46"
                        px={4}
                        py={2}
                        rounded="md"
                        shadow="sm"
                        cursor="pointer"
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        whileHover={{ scale: 1.03, shadow: "md" }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: i * 0.15 }}
                      >
                        <Text fontWeight="semibold">{item}</Text>
                      </MotionBox>
                    ))}
                  </VStack>
                </Box>
              </Box>
            </MotionBox>
          ))}
        </SimpleGrid>

        {/* Application Form */}
        <MotionBox
          py={10}
          px={{ base: 4, md: 16 }}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <Flex
            direction={{ base: "column", md: "row" }}
            align="center"
            justify="center"
            w="95%"
            mx="auto"
            gap={{ base: 6, md: 10 }}
          >
            {/* Form Section */}
            <Box w={{ base: "100%", md: "50%" }}>
              <Box
                maxW="600px"
                mx="auto"
                bg="white"
                p={8}
                rounded="xl"
                shadow="2xl"
              >
                <Heading
                  fontSize={{ base: "2xl", md: "5xl" }}
                  mb={6}
                  textAlign="center"
                  color="#065F46"
                >
                  Apply Now
                </Heading>
                <Box as="form" ref={formRef} onSubmit={sendEmail}>
                  <VStack spacing={4}>
                    <FormControl isRequired>
                      <FormLabel>Name</FormLabel>
                      <Input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Your full name"
                      />
                    </FormControl>
                    <FormControl isRequired>
                      <FormLabel>Email</FormLabel>
                      <Input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="Your email address"
                      />
                    </FormControl>
                    <FormControl isRequired>
                      <FormLabel>Phone</FormLabel>
                      <Input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="Your contact number"
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Cover Letter / Message</FormLabel>
                      <Textarea
                        name="coverLetter"
                        value={formData.coverLetter}
                        onChange={handleChange}
                        placeholder="Write a short message or cover letter"
                      />
                    </FormControl>
                    <FormControl isRequired>
                      <FormLabel>Upload CV (PDF or DOCX)</FormLabel>
                      <Input
                        type="file"
                        name="cv"
                        accept=".pdf,.docx"
                        onChange={handleChange}
                      />
                      <Text fontSize="sm" color="gray.500" mt={1}>
                        (CV will not be attached in email, only file name will
                        be shown)
                      </Text>
                    </FormControl>
                    <Button
                      type="submit"
                      bg="#FBBF24"
                      color="#065F46"
                      w="full"
                      _hover={{ bg: "#F59E0B" }}
                      isLoading={loading}
                      fontSize={"2xl"}
                    >
                      Apply
                    </Button>
                  </VStack>
                </Box>
              </Box>
            </Box>

            {/* Image Section */}
            <Box
              w={{ base: "100%", md: "50%" }}
              h={{ base: "200px", md: "500px" }}
              borderRadius="2xl"
              overflow="hidden"
            >
              <Image
                src={formImage} // replace with your image
                alt="Careers Image"
                objectFit="cover"
                w="100%"
                h="100%"
              />
            </Box>
          </Flex>
        </MotionBox>
      </Box>
    </Box>
  );
}
